import type {
  HyperPlugin,
  HyperClientOptions,
  UniversalResponse,
  PluginContext,
  SendRequest,
  RequestContext,
} from "@hyperttp/types";
import { QueueManager } from "./utils/QueueManager.js";
import type { QueueOptions } from "./types/queue.js";

declare module "@hyperttp/types" {
  interface HyperClientOptions {
    queue?: QueueOptions;
  }
}

const DEFAULT_PARTITION = "default";

export function withQueue(options?: QueueOptions): HyperPlugin {
  const queues = new Map<string, QueueManager>();
  const releases = new WeakMap<object, () => void>();
  let maxConcurrent = 500;
  let partitionKey: QueueOptions["partitionKey"];

  function release(req?: SendRequest, reqCtx?: RequestContext): void {
    const key = reqCtx ?? req;
    if (!key) return;
    releases.get(key)?.();
    releases.delete(key);
  }

  return {
    name: "hyperttp-queue",
    enabled: (config: HyperClientOptions): boolean =>
      config.queue?.enabled ?? options?.enabled ?? options !== undefined,
    setup(ctx: PluginContext): void {
      queues.clear();
      const configured = ctx.config.queue;
      const legacyMaxConcurrent = (
        ctx.config as HyperClientOptions & { network?: { maxConcurrent?: number } }
      ).network?.maxConcurrent;
      maxConcurrent =
        configured?.maxConcurrent ?? options?.maxConcurrent ?? legacyMaxConcurrent ?? 500;
      partitionKey = configured?.partitionKey ?? options?.partitionKey;
    },
    async onRequest(
      req: SendRequest,
      _ctx?: PluginContext,
      reqCtx?: RequestContext,
    ): Promise<void> {
      const signal = req.signal ?? reqCtx?.signal;
      if (signal?.aborted) {
        throw new DOMException("The user aborted a request.", "AbortError");
      }

      const partition = partitionKey?.(req, reqCtx) ?? DEFAULT_PARTITION;
      let queue = queues.get(partition);
      if (!queue) {
        queue = new QueueManager(maxConcurrent);
        queues.set(partition, queue);
      }

      const releaseSlot = await queue.acquire(signal);
      releases.set(reqCtx ?? req, releaseSlot);
    },
    onResponse(
      _res: UniversalResponse,
      req?: SendRequest,
      _ctx?: PluginContext,
      reqCtx?: RequestContext,
    ): void {
      release(req, reqCtx);
    },
    onError(_err: unknown, req?: SendRequest, _ctx?: PluginContext, reqCtx?: RequestContext): void {
      release(req, reqCtx);
    },
  };
}
